import { yupResolver } from '@hookform/resolvers/yup';
import { LoadingButton } from '@mui/lab';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Stack, Typography, useTheme } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { FormProvider, RHFRichTextEditor, Spinner, enqueueSnackbar, getFormattedDateString } from '@zeta-core';
import { MessagingService, getErrorMessage } from '@zeta-services';
import { IMessageDetail, IReplyMessageRequest, ISearchMessageRequest, ISearchMessageResponse, ISendMessageRequest } from '@zeta-types';
import { AxiosResponse } from 'axios';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { FormFields } from './form-fields';
import { extractMessageText, normalizeSlateData } from './helper';
import { MessageDetails } from './message-details';

interface ISecureMessageModal {
  open: boolean;
  handleClose: () => void;
  initialData?: {
    id?: string;
    createdAt?: string;
    subject?: string;
    message?: string;
    reference?: string;
    content?: string;
  } | null;
  entityConfig?: {
    toEntityType: string;
    toEntityID: string;
    toUserID: string;
    fromUserID: string;
    fromName: string;
    toName: string;
    partnerUserName?: string;
    partnerUserID?: string;
  };
  onSuccess: () => void;
  isViewMode?: boolean;
  currentTab: string;
  isClient?: boolean;
  isReadOnly?: boolean;
}

interface IMessageForm {
  from: string;
  subject: string;
  to: string | { label: string; value: string };
  message: {
    type: 'paragraph' | 'bulleted-list' | 'numbered-list' | 'list-item';
    children: { text: string; underline?: boolean }[];
  }[];
  viewMessage: {
    type: 'paragraph' | 'bulleted-list' | 'numbered-list' | 'list-item';
    children: { text: string; underline?: boolean }[];
  }[];
}

const defaultMessage = [{ type: 'paragraph' as const, children: [{ text: '' }] }];

const SecureMessageSchema = Yup.object().shape({
  from: Yup.string().required('From is required'),
  to: Yup.mixed().required('To is required'),
  subject: Yup.string().required('Subject is required'),
  message: Yup.array()
    .of(
      Yup.object().shape({
        type: Yup.string().oneOf(['paragraph', 'bulleted-list', 'numbered-list', 'list-item']).required(),
        children: Yup.array()
          .of(
            Yup.object().shape({
              text: Yup.string().default(''),
              underline: Yup.boolean().optional(),
            })
          )
          .required(),
      })
    )
    .default(defaultMessage),
  viewMessage: Yup.array()
    .of(
      Yup.object().shape({
        type: Yup.string().oneOf(['paragraph', 'bulleted-list', 'numbered-list', 'list-item']).required(),
        children: Yup.array()
          .of(
            Yup.object().shape({
              text: Yup.string().default(''),
              underline: Yup.boolean().optional(),
            })
          )
          .required(),
      })
    )
    .default(defaultMessage),
});

const SecureMessageModal = ({
  open,
  handleClose,
  initialData: currentMessage,
  isViewMode = false,
  entityConfig,
  onSuccess,
  currentTab,
  isClient,
  isReadOnly,
}: ISecureMessageModal) => {
  const theme = useTheme();
  const [isReplying, setIsReplying] = useState(false);
  const [messageHistory, setMessageHistory] = useState<IMessageDetail[]>([]);
  const defaultFormValues = {
    from: entityConfig?.fromName || '',
    to: entityConfig?.toName || '',
    subject: '',
    message: defaultMessage,
    viewMessage: defaultMessage,
  };

  const { mutateAsync: sendMessage, isPending } = useMutation({
    mutationFn: (payload: ISendMessageRequest) => MessagingService().sendMessage(payload),
    onSuccess: () => {
      enqueueSnackbar(`You have successfully sent message to ${entityConfig?.toName}.`, { variant: 'success' });
      onSuccess();
      reset(defaultFormValues);
      setIsReplying(false);
      handleClose();
    },
    onError: (error: any) => {
      const errorCode = error?.response?.data?.error?.errorCode || error?.response?.data?.error[0]?.errorCode;
      enqueueSnackbar(getErrorMessage(errorCode), { variant: 'error' });
    },
  });

  const { mutateAsync: replyMessage, isPending: isReplyingPending } = useMutation({
    mutationFn: (payload: IReplyMessageRequest) => MessagingService().replyMessage(payload),
    onSuccess: () => {
      enqueueSnackbar(`You have successfully sent message to ${entityConfig?.toName}.`, { variant: 'success' });
      onSuccess();
      reset(defaultFormValues);
      setIsReplying(false);
      handleClose();
    },
    onError: (error: any) => {
      const errorCode = error?.response?.data?.error?.errorCode || error?.response?.data?.error[0]?.errorCode;
      enqueueSnackbar(getErrorMessage(errorCode), { variant: 'error' });
    },
  });

  const methods = useForm<IMessageForm>({
    resolver: yupResolver(SecureMessageSchema),
    defaultValues: {
      from: entityConfig?.fromName,
      to: entityConfig?.toName,
      subject: currentMessage?.subject || '',
      message: [{ type: 'paragraph', children: [{ text: '' }] }],
      viewMessage: currentMessage?.content
        ? (() => {
            try {
              const parsedContent = JSON.parse(currentMessage.content || '[]');
              return normalizeSlateData(parsedContent);
            } catch (error) {
              return normalizeSlateData(currentMessage.content || '');
            }
          })()
        : [{ type: 'paragraph', children: [{ text: '' }] }],
    },
    mode: 'onTouched',
    reValidateMode: 'onChange',
  });

  const { handleSubmit, reset } = methods;

  useEffect(() => {
    if (currentMessage) {
      try {
        let parsedContent;
        try {
          parsedContent = JSON.parse(currentMessage.content || '[]');
        } catch (error) {
          parsedContent = currentMessage.content || '';
        }

        const parsedMessage = normalizeSlateData(parsedContent);

        reset(
          {
            from: entityConfig?.fromName || '',
            to: isClient ? entityConfig?.toName || '' : currentMessage?.clientName || '',
            subject: currentMessage?.subject || '',
            message: defaultMessage,
            viewMessage: parsedMessage,
          },
          {
            keepDefaultValues: false,
          }
        );
      } catch (error) {
        console.error('Error processing message content:', error);
        // Fallback to default empty message
        reset(
          {
            from: entityConfig?.fromName || '',
            to: isClient ? entityConfig?.toName || '' : currentMessage?.clientName || '',
            subject: currentMessage.subject || '',
            message: defaultMessage,
            viewMessage: defaultMessage,
          },
          {
            keepDefaultValues: false,
          }
        );
      }
    }
  }, [currentMessage]);

  const onSubmit = async (data: IMessageForm) => {
    if (!entityConfig) {
      enqueueSnackbar('Missing entity configuration', { variant: 'error' });
      return;
    }

    const payload: ISendMessageRequest = {
      toEntityType: entityConfig?.toEntityType as 'Advisor' | 'Retail',
      toEntityID: data?.to?.value && data?.to?.for === 'partner' ? (entityConfig.partnerClientID as string) : (entityConfig?.toEntityID as string),
      toUserID: data?.to?.value ? (data?.to?.value as string) : (entityConfig?.toUserID as string),
      subject: data.subject,
      content: JSON.stringify(data.message),
      textContent: extractMessageText(JSON.stringify(data.message)),
    };
    if (isReplying) {
      const replyPayload: IReplyMessageRequest = {
        messageOwnershipID: currentMessage?.id as string,
        subject: data.subject,
        content: JSON.stringify(data.message),
        textContent: extractMessageText(JSON.stringify(data.message)),
      };
      replyMessage(replyPayload);
    } else {
      sendMessage(payload);
    }
  };

  const handleModalClose = () => {
    reset(defaultFormValues);
    setIsReplying(false);
    handleClose();
  };

  const getMessageHistory = useMutation({
    mutationFn: (payload: ISearchMessageRequest) => MessagingService().searchMessage(payload),
    onSuccess: (response: AxiosResponse<ISearchMessageResponse>) => {
      const filteredMessageHistory = response?.data?.response?.messageDetail.filter((message) => message.id !== currentMessage?.id);
      setMessageHistory(filteredMessageHistory || []);
    },
  });

  const fetchMessages = useCallback(() => {
    const payload: ISearchMessageRequest = {
      sort: '',
      desc: false,
      skipPagination: true,
      reference: currentMessage?.reference,
      referenceUserID: entityConfig?.toUserID,
    };
    getMessageHistory.mutateAsync(payload);
  }, [currentMessage?.reference]);

  useEffect(() => {
    if (isViewMode) {
      fetchMessages();
    }
  }, [fetchMessages, isViewMode]);

  return (
    <Dialog open={open} fullWidth maxWidth="md" onClose={handleModalClose}>
      <DialogTitle>
        <Typography id="heading" variant="h6">
          {isViewMode ? 'Message Details' : 'New Message'}
        </Typography>
      </DialogTitle>

      <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
        {!isViewMode ? (
          <DialogContent>
            <FormFields entityConfig={entityConfig} />
          </DialogContent>
        ) : (
          <DialogContent>
            <Stack mt={1}>
              <RHFRichTextEditor name="viewMessage" disabled={true} />
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" my={1}>
                <Stack direction="row" spacing={1}>
                  <Typography variant="body1" color="text.secondary">
                    Date and time: {getFormattedDateString(currentMessage?.createdAt, 'DD/MM/YYYY HH:mm')}
                  </Typography>
                  |
                  <Typography variant="body1" color="text.secondary">
                    Reference Number: {currentMessage?.reference}
                  </Typography>
                </Stack>
              </Stack>

              <Divider sx={{ my: 2, border: `1px dashed ${theme.palette.grey[300]}` }} />

              {isReplying ? (
                <FormFields />
              ) : getMessageHistory?.isPending ? (
                <Stack alignItems="center" justifyContent="center" height="5vh">
                  <Spinner />
                </Stack>
              ) : (
                <>
                  <Typography variant="subtitle2">Message History ({messageHistory.length})</Typography>

                  {messageHistory.map((message) => (
                    <MessageDetails
                      key={message.id}
                      message={{
                        from: entityConfig?.fromName || '',
                        to: isClient ? entityConfig?.toName || '' : currentMessage?.clientName || '',
                        ...message,
                      }}
                      entityConfig={{
                        fromName: entityConfig?.fromName || '',
                        toName: isClient ? entityConfig?.toName || '' : currentMessage?.clientName || '',
                        fromUserID: entityConfig?.fromUserID || '',
                        toUserID: entityConfig?.toUserID || '',
                      }}
                    />
                  ))}
                </>
              )}
            </Stack>
          </DialogContent>
        )}

        <DialogActions>
          <Button onClick={handleModalClose} variant="outlined">
            Close
          </Button>

          {isViewMode ? (
            isReplying ? (
              <LoadingButton type="submit" variant="contained" loading={isReplyingPending} disabled={isReadOnly}>
                Send Reply
              </LoadingButton>
            ) : (
              <Button variant="contained" onClick={() => setIsReplying(true)} disabled={isReadOnly}>
                Reply
              </Button>
            )
          ) : (
            <LoadingButton type="submit" variant="contained" id="send" loading={isPending} disabled={isReadOnly}>
              Send
            </LoadingButton>
          )}
        </DialogActions>
      </FormProvider>
    </Dialog>
  );
};

export default SecureMessageModal;
